import React, { ReactElement, useEffect } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { useForm } from '@tanstack/react-form'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  clearCourseCapceCreateStatus,
  createCourseV2CAPCEAccreditation,
  fetchCourseV2CAPCEAccreditation,
  selectCourseV2CAPCECreateStatusByCourseID,
} from 'src/v3/content-pipeline/course-pipeline.slice'
import { modalInputSx, modalPaperProps } from 'src/v3/utilities/modal-styles'
import { CAPCE_CLASS_TYPES, CAPCE_TOPIC_AREAS } from '../constants'

type Props = {
  open: boolean
  onClose: () => void
  courseID: string
  courseName?: string
}

export default ({
  open,
  onClose,
  courseID,
  courseName,
}: Props): ReactElement => {
  let dispatch = useAppDispatch()

  let createStatus = useAppSelector(
    selectCourseV2CAPCECreateStatusByCourseID(courseID),
  )
  let loading = createStatus === 'pending'

  let form = useForm({
    defaultValues: {
      courseCode: '',
      expirationDate: '',
      applicationDate: '',
      submissionDate: '',
      topicArea: '',
      classType: '',
      allClearDate: '',
    },
    onSubmit: ({ value }) => {
      dispatch(
        createCourseV2CAPCEAccreditation({
          courseID,
          courseCode: value.courseCode,
          expirationDate: value.expirationDate
            ? new Date(value.expirationDate).toISOString()
            : '',
          applicationDate: value.applicationDate
            ? new Date(value.applicationDate).toISOString()
            : null,
          submissionDate: value.submissionDate
            ? new Date(value.submissionDate).toISOString()
            : null,
          topicArea: value.topicArea,
          classType: value.classType,
          allClearDate: value.allClearDate
            ? new Date(value.allClearDate).toISOString()
            : null,
        }),
      )
    },
  })

  useEffect(() => {
    if (createStatus === 'fulfilled') {
      dispatch(fetchCourseV2CAPCEAccreditation(courseID))
      dispatch(clearCourseCapceCreateStatus(courseID))
      form.reset()
      onClose()
    }
    if (createStatus === 'rejected') {
      dispatch(clearCourseCapceCreateStatus(courseID))
    }
  }, [createStatus, courseID, dispatch, onClose])

  useEffect(() => {
    if (open) {
      let today = new Date().toISOString().split('T')[0]
      let twoYears = new Date(Date.now() + 2 * 365 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0]
      form.setFieldValue('applicationDate', today)
      form.setFieldValue('expirationDate', twoYears)
    }
  }, [open])

  let handleClose = () => {
    form.reset()
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      slotProps={{
        paper: {
          ...modalPaperProps,
          sx: { ...modalPaperProps.sx, borderRadius: 2 },
        },
      }}
      maxWidth="md"
    >
      <DialogTitle>Create CAPCE Accreditation</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 3, mt: 1 }}>
          <Typography color="text.secondary" variant="body2">
            Create CAPCE accreditation for {courseName || 'this course'}. All
            fields are required for compliance approval.
          </Typography>

          <form.Field
            name="courseCode"
            children={(field) => (
              <TextField
                label="Course Code"
                name={field.name}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                required
                fullWidth
                sx={modalInputSx}
                placeholder="e.g., CAPCE12345"
              />
            )}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <form.Field
              name="topicArea"
              children={(field) => (
                <FormControl fullWidth required>
                  <InputLabel>Topic Area</InputLabel>
                  <Select
                    value={field.state.value}
                    label="Topic Area"
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    sx={modalInputSx}
                  >
                    {CAPCE_TOPIC_AREAS.map((area) => (
                      <MenuItem key={area} value={area}>
                        {area}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <form.Field
              name="classType"
              children={(field) => (
                <FormControl fullWidth required>
                  <InputLabel>Class Type</InputLabel>
                  <Select
                    value={field.state.value}
                    label="Class Type"
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    sx={modalInputSx}
                  >
                    {CAPCE_CLASS_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Box>

          <Typography variant="h6" sx={{ mt: 1 }}>
            Important Dates
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <form.Field
              name="applicationDate"
              children={(field) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Application Date"
                    value={field.state.value ? dayjs(field.state.value) : null}
                    onChange={(val) =>
                      field.handleChange(val ? val.format('YYYY-MM-DD') : '')
                    }
                    onClose={field.handleBlur}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: modalInputSx,
                        onBlur: field.handleBlur,
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
            <form.Field
              name="submissionDate"
              children={(field) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Submission Date"
                    value={field.state.value ? dayjs(field.state.value) : null}
                    onChange={(val) =>
                      field.handleChange(val ? val.format('YYYY-MM-DD') : '')
                    }
                    onClose={field.handleBlur}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: modalInputSx,
                        onBlur: field.handleBlur,
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
            <form.Field
              name="expirationDate"
              children={(field) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Expiration Date"
                    value={field.state.value ? dayjs(field.state.value) : null}
                    onChange={(val) =>
                      field.handleChange(val ? val.format('YYYY-MM-DD') : '')
                    }
                    onClose={field.handleBlur}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        sx: modalInputSx,
                        onBlur: field.handleBlur,
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
            <form.Field
              name="allClearDate"
              children={(field) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="All Clear Date"
                    value={field.state.value ? dayjs(field.state.value) : null}
                    onChange={(val) =>
                      field.handleChange(val ? val.format('YYYY-MM-DD') : '')
                    }
                    onClose={field.handleBlur}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: modalInputSx,
                        onBlur: field.handleBlur,
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={() => form.handleSubmit()}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create CAPCE Accreditation'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
